import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Loader2, MessageSquarePlus, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Textarea } from '@/components/atoms/textarea';
import { PhoneInput } from '@/components/molecules/PhoneInput';
import { useCreateMessagingGroup, useMessagingGroupOptions } from '@/lib/messaging/hooks';
import { isApiError } from '@/lib/api/errors';
import { useAuthPermissions } from '@/stores/auth.store';

interface DraftRecipient {
  key: string;
  e164: string;
  displayName: string;
  technicianId?: string;
}

function isSupportedGroupNumber(value: string): boolean {
  const parsed = parsePhoneNumberFromString(value);
  return Boolean(parsed?.isValid() && (parsed.country === 'US' || parsed.country === 'CA'));
}

export function GroupMessagingTrigger() {
  const canCreate = useAuthPermissions().includes('messages:create');
  const [open, setOpen] = useState(false);

  if (!canCreate) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        aria-label="Create a messaging group"
        onClick={() => setOpen(true)}
      >
        <MessageSquarePlus className="h-[18px] w-[18px]" />
      </Button>
      {open ? <CreateMessagingGroupDialog onClose={() => setOpen(false)} /> : null}
    </Dialog>
  );
}

function CreateMessagingGroupDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const optionsQuery = useMessagingGroupOptions(true);
  const createGroup = useCreateMessagingGroup();
  const options = optionsQuery.data;
  const minRecipients = options?.constraints.minRecipients ?? 2;
  const maxRecipients = options?.constraints.maxRecipients ?? 8;
  const [senderId, setSenderId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [search, setSearch] = useState('');
  const [recipients, setRecipients] = useState<DraftRecipient[]>([]);
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');

  useEffect(() => {
    if (!senderId && options?.senders[0]) {
      setSenderId(options.senders[0].id);
    }
  }, [options?.senders, senderId]);

  const selectedSender = options?.senders.find((sender) => sender.id === senderId);
  const visibleTechnicians = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (options?.technicians ?? []).filter(
      (technician) =>
        !needle ||
        technician.name.toLowerCase().includes(needle) ||
        technician.e164.includes(needle.replace(/\D/g, '')),
    );
  }, [options?.technicians, search]);

  function toggleTechnician(technician: { id: string; name: string; e164: string }) {
    const key = `technician:${technician.id}`;
    setRecipients((current) => {
      if (current.some((recipient) => recipient.key === key)) {
        return current.filter((recipient) => recipient.key !== key);
      }
      if (current.length >= maxRecipients) {
        toast.error(`A messaging group can have at most ${maxRecipients} recipients.`);
        return current;
      }
      if (current.some((recipient) => recipient.e164 === technician.e164)) {
        toast.error('That phone number is already in the group.');
        return current;
      }
      return [
        ...current,
        {
          key,
          e164: technician.e164,
          displayName: technician.name,
          technicianId: technician.id,
        },
      ];
    });
  }

  function addManualRecipient() {
    if (!isSupportedGroupNumber(manualPhone)) {
      toast.error('Enter a complete US or Canadian phone number.');
      return;
    }
    if (recipients.some((recipient) => recipient.e164 === manualPhone)) {
      toast.error('That phone number is already in the group.');
      return;
    }
    if (recipients.length >= maxRecipients) {
      toast.error(`A messaging group can have at most ${maxRecipients} recipients.`);
      return;
    }
    setRecipients((current) => [
      ...current,
      {
        key: `manual:${manualPhone}`,
        e164: manualPhone,
        displayName: manualName.trim() || manualPhone,
      },
    ]);
    setManualName('');
    setManualPhone('');
  }

  async function submit() {
    if (!senderId) {
      toast.error('Choose an MMS-enabled sender line.');
      return;
    }
    if (recipients.length < minRecipients || recipients.length > maxRecipients) {
      toast.error(`Choose ${minRecipients}-${maxRecipients} recipients.`);
      return;
    }
    if (selectedSender && recipients.some((recipient) => recipient.e164 === selectedSender.e164)) {
      toast.error('The sending line cannot also be a group recipient.');
      return;
    }
    if (!body.trim()) {
      toast.error('Write the first message before creating the group.');
      return;
    }

    try {
      const result = await createGroup.mutateAsync({
        phoneNumberId: senderId,
        title: title.trim() || undefined,
        recipients: recipients.map((recipient) => ({
          e164: recipient.e164,
          displayName: recipient.displayName,
          technicianId: recipient.technicianId,
        })),
        body: body.trim(),
      });
      toast.success('Messaging group created');
      onClose();
      await navigate({
        to: '/dashboard/inbox',
        search: { view: 'all', q: '', threadId: result.threadId },
      });
    } catch (error) {
      toast.error(
        isApiError(error) && error.message
          ? error.message
          : 'Could not create the messaging group.',
      );
    }
  }

  return (
    <DialogContent className="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle>Create messaging group</DialogTitle>
        <DialogDescription>
          Start one group text where every participant can see and reply to everyone.
        </DialogDescription>
      </DialogHeader>

      {optionsQuery.isLoading ? (
        <div className="flex min-h-52 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading technicians and sender lines…
        </div>
      ) : optionsQuery.isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          Messaging group options could not be loaded. Try closing and reopening this dialog.
        </div>
      ) : !options?.senders.length ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          No accessible MMS-enabled Telnyx line is available. Enable MMS on a phone line and grant
          this user SEND access first.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="group-message-sender">Send from</Label>
              <Select value={senderId} onValueChange={setSenderId}>
                <SelectTrigger id="group-message-sender" className="w-full">
                  <SelectValue placeholder="Choose a line" />
                </SelectTrigger>
                <SelectContent position="popper">
                  {options.senders.map((sender) => (
                    <SelectItem key={sender.id} value={sender.id}>
                      {sender.label} · {sender.e164}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="group-message-title">Group name (optional)</Label>
              <Input
                id="group-message-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={120}
                placeholder="On-call technicians"
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Recipients</Label>
              <span className="text-xs text-muted-foreground">
                {recipients.length}/{maxRecipients}
              </span>
            </div>

            {recipients.length > 0 ? (
              <div className="flex flex-wrap gap-1.5" aria-label="Selected recipients">
                {recipients.map((recipient) => (
                  <span
                    key={recipient.key}
                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-1 text-xs text-primary"
                  >
                    <span>{recipient.displayName}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${recipient.displayName}`}
                      onClick={() =>
                        setRecipients((current) =>
                          current.filter((item) => item.key !== recipient.key),
                        )
                      }
                      className="rounded-full hover:bg-primary/10"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            <div className="rounded-md border">
              <div className="flex items-center border-b px-3">
                <Search className="size-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search technicians"
                  className="border-0 shadow-none focus-visible:ring-0"
                  aria-label="Search technicians"
                />
              </div>
              <div className="max-h-40 overflow-y-auto p-1">
                {visibleTechnicians.length ? (
                  visibleTechnicians.map((technician) => {
                    const checked = recipients.some(
                      (recipient) => recipient.technicianId === technician.id,
                    );
                    const isSender = technician.e164 === selectedSender?.e164;
                    return (
                      <label
                        key={technician.id}
                        className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 hover:bg-muted has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={isSender || (!checked && recipients.length >= maxRecipients)}
                          onCheckedChange={() => toggleTechnician(technician)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">
                            {technician.name}
                          </span>
                          <span className="block text-xs text-muted-foreground">
                            {technician.e164}
                            {isSender ? ' · sending line' : ''}
                          </span>
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <p className="px-2 py-4 text-center text-xs text-muted-foreground">
                    No SMS-enabled technicians found.
                  </p>
                )}
              </div>
            </div>

            <div className="grid items-end gap-2 sm:grid-cols-[1fr_1.4fr_auto]">
              <div className="space-y-1">
                <Label htmlFor="manual-recipient-name" className="text-xs">
                  Other person
                </Label>
                <Input
                  id="manual-recipient-name"
                  value={manualName}
                  onChange={(event) => setManualName(event.target.value)}
                  maxLength={120}
                  placeholder="Name (optional)"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="manual-recipient-phone" className="text-xs">
                  Mobile number
                </Label>
                <PhoneInput
                  id="manual-recipient-phone"
                  value={manualPhone}
                  onChange={setManualPhone}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addManualRecipient}
                disabled={!manualPhone || recipients.length >= maxRecipients}
              >
                <Plus className="mr-1 size-4" />
                Add
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="group-message-body">First message</Label>
              <span className="text-xs text-muted-foreground">{body.length}/1600</span>
            </div>
            <Textarea
              id="group-message-body"
              value={body}
              onChange={(event) => setBody(event.target.value)}
              maxLength={1600}
              rows={4}
              placeholder="Message the team…"
            />
          </div>

          <p className="rounded-md bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground">
            Group texts use MMS and support US/Canadian mobile handsets. Every participant sees
            every reply. Telnyx charges one MMS per recipient; carrier delivery confirmation may
            remain “sent” for non-Telnyx numbers.
          </p>
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => void submit()}
          disabled={
            createGroup.isPending ||
            !options?.senders.length ||
            !body.trim() ||
            recipients.length < minRecipients
          }
        >
          {createGroup.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Create and send
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
